import { useState, useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { RiInformationLine } from "react-icons/ri";
import FileUploadCard from "../file-upload-card";
import { validateMySQLUrl } from "../utils/url-validator";
import { FiCheckCircle } from "react-icons/fi";
import { FiXCircle } from "react-icons/fi";
import { FileAttachment } from "~/stores/slices/connection-attachments";
import { useGlobalStore } from "~/stores/store-provider";

interface DatabaseConnectionForm {
  url: string;
}

interface MySQLConnectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MySQLConnector = ({ open, onOpenChange }: MySQLConnectorProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const [formData, setFormData] = useState<DatabaseConnectionForm>({
    url: "",
  });
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCalloutExiting, setIsCalloutExiting] = useState<boolean>(false);
  const { addConnection, attachments, deleteAllAttachments, resetAttachments } =
    useGlobalStore((state) => state);

  // Handle dialog open/close with native methods
  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.showModal();
    } else if (!open && modalRef.current) {
      modalRef.current.close();
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        url: "",
      });
      setError(false);
      setIsLoading(false);
      setIsConnected(null);
      setIsCalloutExiting(false);
      resetAttachments();
    }
  }, [open, resetAttachments]);

  const closeModal = async () => {
    // Delete all attachments when modal closes to prevent orphaned files
    await deleteAllAttachments();
    modalRef.current?.close();
    onOpenChange(false);
  };

  const handleInputChange = (value: string) => {
    setFormData({
      ...formData,
      url: value,
    });

    // Validate URL and set error if invalid
    if (value.trim() === "") {
      setError(false);
    } else {
      const validatedData = validateMySQLUrl(value);
      setError(!validatedData.isValid);
    }
  };

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      const validatedData = validateMySQLUrl(formData.url).parsed;
      const attachmentData = attachments.map((attachment: FileAttachment) => ({
        description: attachment.description,
        file_id: attachment.fileId,
        file_name: attachment.fileName,
      }));

      await addConnection({ ...validatedData, attachments: attachmentData });

      // Clear form data after successful connection
      setFormData({
        url: "",
      });
      resetAttachments();

      setIsConnected(true);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 2700);

      setTimeout(() => {
        onOpenChange(false);
      }, 2000);
    } catch (error) {
      setIsLoading(false);
      setIsConnected(false);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 4000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Connect to MySQL</h3>
          <button
            className="btn btn-sm btn-circle btn-ghost"
            onClick={closeModal}
          >
            <IoClose />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <label className="label">
              <div className="flex items-center gap-1">
                <span className="label-text font-medium">Database URL</span>
                <div
                  className="tooltip tooltip-right"
                  data-tip="Your MySQL database URL in the format mysql://username:password@host:port/database."
                >
                  <RiInformationLine className="text-gray-500" />
                </div>
              </div>
            </label>
            <input
              type="text"
              className={`input input-bordered w-full mt-2 ${
                error ? "input-error" : ""
              }`}
              placeholder="mysql://username:password@host:port/database"
              value={formData.url}
              onChange={(e) => handleInputChange(e.target.value)}
            />
            {error && (
              <p className="text-sm text-red-500 mt-1">
                Connection string error: please check the URL syntax.
              </p>
            )}
          </div>
          <FileUploadCard />
          <button
            className="mt-2 btn btn-neutral bg-orange-600 text-white hover:bg-orange-500"
            onClick={handleConnect}
            disabled={error || formData.url.trim() === "" || isLoading}
          >
            {isLoading ? (
              <span className="loading loading-spinner"></span>
            ) : (
              "Connect"
            )}
          </button>
        </div>
      </div>
      {isConnected !== null && (
        <div className="toast toast-top toast-center z-50">
          <div
            className={`alert ${
              isConnected ? "alert-success" : "alert-error"
            } ${
              isCalloutExiting
                ? "opacity-0 -translate-y-full"
                : "opacity-100 translate-y-0"
            } transition-all duration-300 ease-in-out`}
          >
            {isConnected ? (
              <FiCheckCircle className="h-6 w-6" />
            ) : (
              <FiXCircle className="h-6 w-6" />
            )}
            <span>
              {isConnected ? "Connection successful" : "Connection failed"}
            </span>
          </div>
        </div>
      )}
    </dialog>
  );
};

export default MySQLConnector;
